import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardContent, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const PAGE_SIZE = 50;

const AdminUsersPanel = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [loading, setLoading] = useState(false);

  // debounce search input — avoids hammering the API while typing
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedQuery(query);
      setPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [query]);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(PAGE_SIZE));
      if (debouncedQuery) params.set('q', debouncedQuery);
      const r = await fetch(`${API_BASE}/admin/users?${params.toString()}`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setUsers(j.users || []);
      setTotal(j.total || 0);
    } catch (e) {
      console.error('Failed to fetch users:', e);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedQuery]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  const updateRole = async (userId, role) => {
    try {
      await fetch(`${API_BASE}/admin/users/${userId}/role`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      });
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role } : u)));
    } catch (e) {
      console.error('Failed to update role:', e);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, total);

  return (
    <Card className="rounded-none" data-testid="admin-users-panel">
      <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 space-y-0">
        <CardTitle className="text-lg">
          {t('admin.users_label', 'Utilisateurs')} ({total.toLocaleString('fr-FR')})
        </CardTitle>
        <div className="relative w-full md:w-80">
          <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher (email, nom, société, téléphone…)"
            className="pl-8 rounded-none"
            data-testid="admin-users-search"
          />
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <p className="text-muted-foreground text-center py-8">Chargement…</p>
        ) : users.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            {debouncedQuery ? 'Aucun résultat pour cette recherche.' : t('admin.no_users', 'Aucun utilisateur')}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2 px-3 font-semibold">{t('admin.email', 'Email')}</th>
                    <th className="py-2 px-3 font-semibold">{t('admin.name', 'Nom')}</th>
                    <th className="py-2 px-3 font-semibold">Société</th>
                    <th className="py-2 px-3 font-semibold">{t('admin.role', 'Rôle')}</th>
                    <th className="py-2 px-3 font-semibold">Origine</th>
                    <th className="py-2 px-3 font-semibold">{t('admin.registration', 'Inscription')}</th>
                    <th className="py-2 px-3 font-semibold">{t('admin.action', 'Action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id || u.email} className="border-b hover:bg-gray-50" data-testid={`user-row-${u.id || u.email}`}>
                      <td className="py-2 px-3">{u.email}</td>
                      <td className="py-2 px-3">{u.name || (u.first_name || u.last_name ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : '-')}</td>
                      <td className="py-2 px-3 text-muted-foreground">{u.company || '-'}</td>
                      <td className="py-2 px-3">
                        <span className={`text-xs px-2 py-0.5 font-medium ${u.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>
                          {u.role === 'admin' ? t('admin.role_admin', 'Admin') : t('admin.role_client', 'Client')}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        {u.migrated_from_wordpress ? (
                          <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700">WordPress</span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 bg-gray-50 text-gray-600">Natif</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : '-'}</td>
                      <td className="py-2 px-3">
                        <Select value={u.role || 'customer'} onValueChange={(v) => updateRole(u.id, v)}>
                          <SelectTrigger className="rounded-none w-[110px] h-7 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="customer">{t('admin.role_client', 'Client')}</SelectItem>
                            <SelectItem value="admin">{t('admin.role_admin', 'Admin')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4 text-sm">
              <div className="text-muted-foreground">
                Affichage {start}–{end} sur <strong>{total.toLocaleString('fr-FR')}</strong>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline" size="sm" className="rounded-none gap-1"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1 || loading}
                  data-testid="users-prev-page"
                >
                  <ChevronLeft className="w-4 h-4" /> Précédent
                </Button>
                <span className="px-3 py-1 bg-gray-100">Page {page} / {totalPages}</span>
                <Button
                  variant="outline" size="sm" className="rounded-none gap-1"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || loading}
                  data-testid="users-next-page"
                >
                  Suivant <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminUsersPanel;
